import React, { useState, useEffect, useRef } from "react";
import * as planService from "../services/planService";
import "./TaskEditor.css";

export interface TaskEditorProps {
  data?: any;
  onSave?: (task: any) => void;
  onCancel?: () => void;
  onDelete?: () => void;
  hasChildren?: boolean;
  planId?: string;
}

const colorOptions = [
  { value: "#2196F3", label: "Blue" },
  { value: "#4CAF50", label: "Green" },
  { value: "#FF9800", label: "Orange" },
  { value: "#F44336", label: "Red" },
  { value: "#9f27b3", label: "Purple" },
  { value: "#DB7DC5", label: "Pink" },
  { value: "#795548", label: "Brown" },
];

const statusOptions = [
  { value: "", label: "None", color: "transparent" },
  { value: "in_progress", label: "WIP", color: "#1976D2" },
  { value: "on_hold", label: "HOLD", color: "#E65100" },
  { value: "at_risk", label: "RISK", color: "#C62828" },
  { value: "delayed", label: "DELAY", color: "#6A1B9A" },
  { value: "done", label: "DONE", color: "#2E7D32" },
  { value: "cancelled", label: "CANCEL", color: "#555" },
];

const typeOptions = [
  { value: "task", label: "Task" },
  { value: "project", label: "Project" },
  { value: "milestone", label: "Milestone" },
  { value: "marker", label: "Marker" },
];

function getDefaultColor(type: string) {
  if (type === "project") return "#4CAF50";
  if (type === "milestone") return "#DB7DC5";
  if (type === "marker") return "#F44336";
  return "#2196F3";
}

function formatDate(d: Date | string | undefined): string {
  if (!d) return "";
  const date = typeof d === "string" ? new Date(d) : d;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const TaskEditor: React.FC<TaskEditorProps> = ({ data, onSave, onCancel, onDelete, hasChildren, planId }) => {
  const [text, setText] = useState(data?.text === "New task" ? "" : (data?.text || ""));
  const [type, setType] = useState(data?.type || "task");
  const [color, setColor] = useState(data?.color || getDefaultColor(data?.type || "task"));
  const [assignedTo, setAssignedTo] = useState(data?.assigned_to || "");
  const [startDate, setStartDate] = useState(formatDate(data?.start_date));
  const [endDate, setEndDate] = useState(formatDate(data?.end_date));
  const [progress, setProgress] = useState(Math.round((data?.progress || 0) * 100));
  const [status, setStatus] = useState(data?.status || "");
  const [attachments, setAttachments] = useState<planService.Attachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [confirmDeleteAttId, setConfirmDeleteAttId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canShowAttachments = planId && data?.id && !data?.$new && type !== "marker";

  useEffect(() => {
    if (canShowAttachments) {
      planService.listAttachments(planId!, data.id).then(setAttachments).catch(console.error);
    }
  }, [canShowAttachments, planId, data?.id]);

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !planId || !data?.id) return;
    setUploading(true);
    try {
      const att = await planService.uploadAttachment(planId, data.id, file);
      setAttachments(prev => [att, ...prev]);
    } catch (err) {
      console.error("Upload failed:", err);
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  async function handleDeleteAttachment(attId: string) {
    if (!planId || !data?.id) return;
    try {
      await planService.deleteAttachment(planId, data.id, attId);
      setAttachments(prev => prev.filter(a => a.id !== attId));
    } catch (err) {
      console.error("Delete failed:", err);
    }
    setConfirmDeleteAttId(null);
  }

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel?.();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onCancel]);

  function handleTypeChange(newType: string) {
    setType(newType);
    setColor(getDefaultColor(newType));
  }

  function handleSave() {
    const isSingle = type === "milestone" || type === "marker";
    if (!text.trim() || dateMissing || dateError) return;
    const updated = {
      ...data,
      text: text.trim(),
      type,
      color,
      assigned_to: assignedTo,
      start_date: startDate ? new Date(startDate + "T00:00:00") : data?.start_date,
      end_date: endDate ? new Date(endDate + "T00:00:00") : undefined,
      progress: progress / 100,
      status,
    };
    if (type === "milestone" || type === "marker") {
      updated.duration = 0;
    }
    onSave?.(updated);
  }

  const isNew = data?.$new;
  const isMarker = type === "marker";
  const isMilestone = type === "milestone";
  const isSingleDate = isMilestone || isMarker;
  const dateError = !isSingleDate && startDate && endDate && endDate <= startDate;
  const dateMissing = !startDate || (!isSingleDate && !endDate);
  const showProgress = !isMilestone && !isMarker;
  const showAssigned = !isMarker && !isMilestone;

  const headerLabel = isMarker
    ? (isNew ? "New Marker" : "Edit Marker")
    : (isNew ? "New Task" : "Edit Task");

  return (
    <div className="task-editor-overlay" onClick={onCancel}>
      <div className="task-editor" onClick={(e) => e.stopPropagation()}>
        <div className="task-editor-header">
          <h2>{headerLabel}</h2>
          <button className="task-editor-close" onClick={onCancel}>&#x2715;</button>
        </div>

        <div className="task-editor-body">
          <div className="task-editor-field">
            <label>{isMarker ? "Label" : "Name"}</label>
            <input
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              autoFocus
              placeholder={isMarker ? "Marker label" : "Task name"}
            />
          </div>

          <div className="task-editor-field">
            <label>Type</label>
            <div className="task-editor-type-pills">
              {typeOptions.map((opt) => {
                const disabled = opt.value === "marker" && hasChildren;
                return (
                  <button
                    key={opt.value}
                    className={`task-editor-pill ${type === opt.value ? "active" : ""} ${disabled ? "disabled" : ""}`}
                    onClick={() => !disabled && handleTypeChange(opt.value)}
                    title={disabled ? "Cannot convert to marker with subtasks" : ""}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {showAssigned && (
            <div className="task-editor-field">
              <label>Assigned To</label>
              <input
                type="text"
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value)}
                placeholder="Name"
              />
            </div>
          )}

          <div className="task-editor-field">
            <label>Color</label>
            <div className="task-editor-colors">
              {colorOptions.map((opt) => (
                <button
                  key={opt.value}
                  className={`task-editor-color-btn ${color === opt.value ? "selected" : ""}`}
                  style={{ background: opt.value }}
                  title={opt.label}
                  onClick={() => setColor(opt.value)}
                />
              ))}
            </div>
          </div>

          {!isMarker && !isMilestone && (
            <div className="task-editor-field">
              <label>Status</label>
              <div className="task-editor-status-tags">
                {statusOptions.map((opt) => (
                  <button
                    key={opt.value}
                    className={`task-editor-status-tag ${status === opt.value ? "active" : ""}`}
                    style={opt.color !== "transparent" ? { background: opt.color, color: "#fff" } : undefined}
                    onClick={() => setStatus(opt.value)}
                  >
                    {opt.label || "None"}
                  </button>
                ))}
              </div>
            </div>
          )}

          {!isSingleDate && (
            <div className="task-editor-row">
              <div className="task-editor-field task-editor-field-half">
                <label>Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="task-editor-field task-editor-field-half">
                <label>End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={dateError ? { borderColor: "#e53935" } : undefined}
                />
                {dateError && (
                  <span style={{ fontSize: 12, color: "#e53935" }}>End date must be after start date</span>
                )}
              </div>
            </div>
          )}

          {isSingleDate && (
            <div className="task-editor-field">
              <label>Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
          )}

          {showProgress && (
            <div className="task-editor-field">
              <label>Progress — {progress}%</label>
              <input
                type="range"
                min={0}
                max={100}
                value={progress}
                onChange={(e) => setProgress(Number(e.target.value))}
                className="task-editor-slider"
              />
            </div>
          )}
          {canShowAttachments && (
            <div className="task-editor-field">
              <label>Attachments</label>
              <div className="task-editor-attachments">
                {attachments.map((att) => (
                  <div key={att.id} className="task-editor-attachment">
                    <a href={att.blobUrl} target="_blank" rel="noopener noreferrer" className="task-editor-attachment-name">
                      {att.name}
                    </a>
                    <span className="task-editor-attachment-size">
                      {att.size < 1024 ? `${att.size} B` : att.size < 1048576 ? `${Math.round(att.size / 1024)} KB` : `${(att.size / 1048576).toFixed(1)} MB`}
                    </span>
                    {confirmDeleteAttId === att.id ? (
                      <span className="task-editor-attachment-confirm">
                        <button onClick={() => handleDeleteAttachment(att.id)} className="task-editor-attachment-confirm-yes">Delete</button>
                        <button onClick={() => setConfirmDeleteAttId(null)} className="task-editor-attachment-confirm-no">Cancel</button>
                      </span>
                    ) : (
                      <button
                        className="task-editor-attachment-delete"
                        onClick={() => setConfirmDeleteAttId(att.id)}
                        title="Remove"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
                <input
                  ref={fileInputRef}
                  type="file"
                  style={{ display: "none" }}
                  onChange={handleFileUpload}
                />
                <button
                  className="task-editor-attachment-add"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? "Uploading..." : "+ Add File"}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="task-editor-footer">
          <div className="task-editor-footer-left">
            {!isNew && (
              <button className="task-editor-btn task-editor-btn-delete" onClick={onDelete}>
                Delete
              </button>
            )}
          </div>
          <div className="task-editor-footer-right">
            <button className="task-editor-btn task-editor-btn-cancel" onClick={onCancel}>
              Cancel
            </button>
            <button className="task-editor-btn task-editor-btn-save" onClick={handleSave} disabled={!text.trim() || !!dateError || !!dateMissing}>
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskEditor;
